import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { getFirebaseAuth } from '@/config/firebase';
import { employeeApi } from '@/api/employee.api';
import { useAuthStore } from '@/store/auth.store';
import { cancelAllPendingRequests } from '@/lib/axios';
import { TalentChekRole, type UserProfile } from '@/types/auth.types';
import type { ApiEmployee } from '@/types/employee.types';
import { employeeCountry } from '@/features/employees/utils/employeeVisibility';
import { countryCodeFromName } from '@/config/countries';
import { queryClient } from '@/lib/queryClient';
import {
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY,
  V2_USER_KEY,
  USER_PROFILE_KEY,
  USER_AUTHORITY_KEY,
} from '@/config/constants';
import { removeItem } from '@/utils/storage.util';

// Preserve the corporate domains supported by the existing login.
const domains = new Set([
  'autochek.africa',
  'kifal-auto.ma',
  'kifal.ma',
  'kifal.net',
  'coinafrique.com',
]);
const noAccess = 'You do not have access to Talentchek. Please contact your administrator.';
const normalize = (value: string) => value.trim().toLowerCase();
export function employeeRoles(employeeRole: ApiEmployee['employeeRole']): TalentChekRole[] {
  const aliases: Record<string, TalentChekRole> = {
    STAFF: TalentChekRole.STAFF,
    EMPLOYEE: TalentChekRole.STAFF,
    ADMIN: TalentChekRole.ADMIN,
    GLOBAL_HR: TalentChekRole.GLOBAL_HR,
    COUNTRY_HR: TalentChekRole.COUNTRY_HR,
  };
  return [
    ...new Set(
      (Array.isArray(employeeRole) ? employeeRole : [employeeRole]).flatMap(value => {
        if (typeof value !== 'string') return [];
        const key = value
          .trim()
          .toUpperCase()
          .replace(/[\s-]+/g, '_');
        const resolved = Object.values(TalentChekRole).find(item => item === key) ?? aliases[key];
        return resolved ? [resolved] : [];
      })
    ),
  ].sort((a, b) => {
    const order = [
      TalentChekRole.ADMIN,
      TalentChekRole.GLOBAL_HR,
      TalentChekRole.COUNTRY_HR,
      TalentChekRole.STAFF,
    ];
    return order.indexOf(a) - order.indexOf(b);
  });
}

/**
 * Every email Firebase reports for the account (`providerData` mirrors the `providerUserInfo`
 * array of the account lookup response) that belongs to an allowed company domain. Some users
 * hold different emails across providers, so the first one cannot be assumed to be the company
 * address. Google provider emails come first, then the remaining providers, then (only when no
 * provider reports an email) the account email. The Google provider itself is still required.
 */
export function verifiedCompanyEmails(
  user: Pick<User, 'email' | 'emailVerified' | 'providerData'>
): string[] {
  const providers = (user.providerData ?? []) as { providerId?: string; email?: string | null }[];
  const isGoogle = (item: { providerId?: string }) =>
    item.providerId === 'google.com' || item.providerId === 'google';
  const hasGoogleProvider = providers.some(isGoogle);

  // 1. Pick all emails from Google providers that belong to allowed company domains.
  // Google OAuth directly validates email ownership, so all Google provider company emails are trusted.
  const googleEmails = [
    ...new Set(
      providers
        .filter(isGoogle)
        .map(item => normalize(item.email ?? ''))
        .filter(email => email && domains.has(email.split('@')[1]))
    ),
  ];

  // 2. Pick fallback candidate emails from other providers or account-level email.
  // Security guard: Fallback emails from non-Google providers (e.g. password) are only
  // accepted if the account's email has been verified by Firebase (emailVerified === true).
  const otherProviderEmails = providers
    .filter(item => !isGoogle(item))
    .map(item => normalize(item.email ?? ''))
    .filter(email => email && domains.has(email.split('@')[1]));

  const rawUserEmail = normalize(user.email ?? '');
  const hasProviderEmails = providers.some(item => normalize(item.email ?? ''));
  const accountEmail =
    !hasProviderEmails && rawUserEmail && domains.has(rawUserEmail.split('@')[1])
      ? [rawUserEmail]
      : [];

  const fallbackCandidates = [...otherProviderEmails, ...accountEmail];
  const verifiedFallbackEmails = user.emailVerified
    ? [...new Set(fallbackCandidates)]
    : [];

  // Order: Google provider emails checked first, followed by any verified fallback emails.
  const allowed = [...new Set([...googleEmails, ...verifiedFallbackEmails])];

  if (!hasGoogleProvider || !allowed.length) {
    throw new Error(
      'Please sign in with your company Google account. Contact your administrator if you need access.'
    );
  }
  return allowed;
}

export function validateEmployee(
  user: Pick<User, 'email' | 'emailVerified' | 'providerData'>,
  employee: ApiEmployee | null
) {
  const emails = verifiedCompanyEmails(user);
  // Only a company email identifies an employee; never infer identity from employee name.
  if (!employee?.id || !emails.includes(normalize(employee.workEmail ?? ''))) {
    throw new Error(noAccess);
  }
  if (normalize(employee.status ?? '') !== 'active') {
    throw new Error(
      'Your account has been deactivated or is not active. Please contact your administrator.'
    );
  }
  const roles = employeeRoles(employee.employeeRole);
  if (!roles.length) throw new Error(noAccess);
  if (
    roles.includes(TalentChekRole.COUNTRY_HR) &&
    !roles.includes(TalentChekRole.GLOBAL_HR) &&
    !roles.includes(TalentChekRole.ADMIN) &&
    !employeeCountry(employee)
  ) {
    throw new Error('Your country has not been configured. Please contact your administrator.');
  }
  return { employee, roles };
}

export function googleAuthError(error: unknown): string {
  const code = (error as { code?: string })?.code;
  const messages: Record<string, string> = {
    'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again when you are ready.',
    'auth/cancelled-popup-request':
      'Another sign-in window is already open. Please use that window.',
    'auth/popup-blocked':
      'Your browser blocked the Google sign-in window. Allow pop-ups for this site and try again.',
    'auth/unauthorized-domain':
      'Google sign-in is not enabled for this website. Please contact your administrator.',
    'auth/operation-not-allowed':
      'Google sign-in has not been enabled. Please contact your administrator.',
    'auth/network-request-failed':
      'Unable to connect to Google. Check your connection and try again.',
  };
  if (code)
    return (
      messages[code] ?? 'Google sign-in failed. Please try again or contact your administrator.'
    );
  return error instanceof Error ? error.message : 'Unable to sign in. Please try again.';
}

export function clearLegacySession() {
  [
    TOKEN_KEY,
    REFRESH_TOKEN_KEY,
    USER_KEY,
    V2_USER_KEY,
    USER_PROFILE_KEY,
    USER_AUTHORITY_KEY,
  ].forEach(removeItem);
}
let pending: { user: User; promise: Promise<void> } | null = null;
async function establishSession(user: User): Promise<void> {
  // The Firebase observer and popup completion share the same validation request.
  if (pending?.user === user) return pending.promise;
  const promise = (async () => {
    const emails = verifiedCompanyEmails(user);
    let record: ApiEmployee | null = null;
    try {
      // Try each allowed email until one matches an employee record.
      for (const email of emails) {
        record = await employeeApi.getByWorkEmail(email);
        if (record) break;
      }
    } catch {
      throw new Error(
        'Unable to verify your employee account. Please try again or contact your administrator.'
      );
    }
    const { employee, roles } = validateEmployee(user, record);
    if (getFirebaseAuth().currentUser?.uid !== user.uid) return;
    const profile: UserProfile = {
      id: String(employee.id),
      firstname: employee.firstName ?? '',
      lastname: employee.lastName ?? '',
      middlename: employee.middleName ?? '',
      primaryEmail: employee.workEmail!.trim(),
      primaryPhone: employee.workPhone ?? '',
      country: countryCodeFromName(employeeCountry(employee)),
      emailVerified: true,
      phoneVerified: false,
      accountStatus: employee.status!,
      createdAt: employee.createdAt ?? '',
      updatedAt: employee.updatedAt ?? '',
      authType: 'google',
      partnerId: '',
      area: '',
      territories: '',
      roles,
      deviceTokens: [],
    };
    clearLegacySession();
    if (useAuthStore.getState().authenticatedEmployee?.id !== employee.id) queryClient.clear();
    useAuthStore.getState().setGoogleAuth(employee, profile, { roles, permissions: [] });
  })();
  pending = { user, promise };
  try {
    await promise;
  } finally {
    if (pending?.promise === promise) pending = null;
  }
}

export const googleAuthService = {
  async signIn(emailHint = '') {
    const auth = getFirebaseAuth();
    useAuthStore.getState().setAuthError(null);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
      ...(emailHint.trim() ? { login_hint: emailHint.trim() } : {}),
    });
    const result = await signInWithPopup(auth, provider);
    // Log useful response metadata without exposing access/refresh tokens or credentials.
    console.info('Firebase Google sign-in response', {
      uid: result.user.uid,
      email: result.user.email,
      emailVerified: result.user.emailVerified,
      providerId: result.providerId,
      operationType: result.operationType,
    });
    try {
      await establishSession(result.user);
    } catch (error) {
      await googleAuthService.rejectSession(error);
      throw error;
    }
  },
  observe() {
    clearLegacySession();
    try {
      return onAuthStateChanged(
        getFirebaseAuth(),
        user => {
          if (!user) {
            useAuthStore.getState().clearAuth();
            queryClient.clear();
            return;
          }
          // Clear the previous identity before validating a changed Google session.
          // Stay in the loading state while re-validating so route guards do not
          // redirect a refreshed page to /login (and then the dashboard).
          useAuthStore.getState().resetIdentity();
          useAuthStore.getState().setLoading(true);
          // Never grant access from a cached employee or an old backend token.
          void establishSession(user).catch(error => googleAuthService.rejectSession(error));
        },
        error => {
          useAuthStore.getState().clearAuth();
          useAuthStore.getState().setAuthError(googleAuthError(error));
        }
      );
    } catch (error) {
      useAuthStore.getState().clearAuth();
      useAuthStore.getState().setAuthError(googleAuthError(error));
      return () => {};
    }
  },
  async rejectSession(error: unknown) {
    useAuthStore.getState().clearAuth();
    useAuthStore.getState().setAuthError(googleAuthError(error));
    queryClient.clear();
    try {
      await signOut(getFirebaseAuth());
    } catch {
      /* Access remains denied locally. */
    }
  },
  async logout() {
    // 1. Cancel in-flight API requests so stale responses cannot repopulate state.
    cancelAllPendingRequests();
    // 2. Sign out of Firebase (fires onAuthStateChanged(null) → clearAuth).
    const auth = getFirebaseAuth();
    await signOut(auth);
    // 3. Remove persisted legacy session artifacts.
    clearLegacySession();
    // 4. Drop all cached query data.
    queryClient.clear();
    // 5. Reset the auth store.
    useAuthStore.getState().clearAuth();
    useAuthStore.getState().setAuthError(null);
    // 6. Confirm completion.
    console.info('Firebase sign-out completed', { signedOut: auth.currentUser === null });
  },
};
